import { Chain } from '@ankr.com/chains-list';
import { OverlaySpinner } from '@ankr.com/ui';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { GuardResolution } from 'modules/common/components/GuardResolution';
import { Placeholder } from 'modules/common/components/Placeholder';
import { ProjectStatsProvider } from 'modules/projects/components/ProjectStatsProvider';
import { TabSize } from 'modules/common/components/SecondaryTab';
import { TimeframeTabs } from 'modules/chains/components/TimeframeTabs';
import { useTranslation } from 'modules/i18n/hooks/useTranslation';

import { ChainProjectItem } from './ChainProjectItem';
import { chainProjectsSectionTranslation } from './translation';
import { useChainProjects } from './useChainProjects';
import { useChainProjectsSectionStyles } from './useChainProjectsSectionStyles';

export interface IChainProjectsSectionProps {
  chain: Chain;
}

export const ChainProjectsSection = ({ chain }: IChainProjectsSectionProps) => {
  const {
    projects,
    projectsLoading,
    projectsStatsParams,
    shouldShowTokenManager,
    timeframe,
    timeframeTabs,
  } = useChainProjects({
    chainId: chain.id,
  });

  const { classes, cx } = useChainProjectsSectionStyles();

  const { keys, t } = useTranslation(chainProjectsSectionTranslation);

  if (!shouldShowTokenManager) {
    return null;
  }

  return (
    <Paper className={classes.chainProjectsRoot}>
      <div className={classes.projectsSectionTitle}>
        <Typography variant="subtitle2" color="textSecondary">
          {t(keys.projects)}
        </Typography>
        <TimeframeTabs
          className={classes.chainProjectsTimeframe}
          tabClassName={classes.chainProjectsTimeframeTab}
          tabs={timeframeTabs}
          timeframe={timeframe}
          size={TabSize.Smallest}
        />
      </div>
      <Table className={classes.projectsTable}>
        <GuardResolution protectedResolution="smDown">
          <TableHead className={classes.projectsTableHeader}>
            <TableRow>
              <TableCell
                className={cx(
                  classes.projectsTableCell,
                  classes.projectTableHeadCell,
                  classes.projectTableHeadCellNameCell,
                )}
              >
                {t(keys.name)}
              </TableCell>
              <TableCell
                className={cx(
                  classes.projectsTableCell,
                  classes.projectTableHeadCell,
                  classes.projectsTableRequestsCell,
                )}
              >
                {t(keys.requests)}
              </TableCell>
              <TableCell
                className={cx(
                  classes.projectsTableCell,
                  classes.projectTableHeadCell,
                  classes.projectsTableButtonsCell,
                )}
                align="right"
              />
            </TableRow>
          </TableHead>
        </GuardResolution>
        <TableBody>
          <Placeholder
            hasPlaceholder={projectsLoading}
            placeholder={
              <TableRow>
                <TableCell colSpan={4}>
                  <OverlaySpinner />
                </TableCell>
              </TableRow>
            }
          >
            <ProjectStatsProvider value={projectsStatsParams}>
              {projects.map(project => (
                <ChainProjectItem
                  chain={chain}
                  jwt={project}
                  jwts={projects}
                  key={project.index}
                  timeframe={timeframe}
                />
              ))}
            </ProjectStatsProvider>
          </Placeholder>
        </TableBody>
      </Table>
    </Paper>
  );
};
